import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import Hashids from "hashids"
import { sprintf } from "sprintf-js"

import { RootState, displayNotice } from "./../../globals"
import { getAccessPermissions, hashCode, operatorList } from "./../../common"
import { ENROLLMENT_STATUS } from "./../../constants"
import { cursosDeAlumno } from "./../../utils/cursoCorto"
import CatalogService from "./../../services/catalog.service"
import StudentsService from "./../../services/campus/students.service"
import StudentInfoModal from "./studentInfoModal"

export interface PerfilAlumnoModalProps {
	studentId: number
	campusId: number
	onClose: () => void
}

/*
	El perfil del alumno EN VENTANA, sin salir de la pantalla donde se está (Esteban, 2026-09-23:
	"Ver Perfil" en Pagos por Procesar lo mandaba al módulo Estudiantes en vez de abrir el perfil).

	Es la misma ventana de Estudiantes (StudentInfoModal) con los mismos datos: se trae al alumno
	con la consulta del listado y se le da la misma forma que le da students.tsx (showPrompt y
	abrirPerfilPorEnlace). Se copió aquí en vez de mover students.tsx para no tocar ese módulo;
	si allá cambia la forma de los datos, hay que cambiarla aquí también.
*/
const sinNinguno = (grados: any[]) => (grados || []).filter((g: any) => Number(g.id) !== 0)

const conAgenda = (inscripciones: any[] | undefined, hashIds: any) =>
	(inscripciones || []).map((enrollment: any) => {
		const refIndex = "S_" + enrollment.schedule.id
		const sessionIndex: any = {}

		enrollment.schedule.sessions.forEach((session: any) => { sessionIndex["S_" + session.week_day] = session })

		return {
			...enrollment.schedule, refIndex: refIndex,
			sessionRef: sessionIndex,
			color: sprintf
				(
					"#%02X%02X%02X",
					(hashCode(hashIds.encode(enrollment.schedule.id)) & 255),
					(hashCode(enrollment.schedule.level.name) & 255),
					(hashCode(enrollment.schedule.level.course.name) & 255)
				)
		}
	})

const PerfilAlumnoModal: React.FC<PerfilAlumnoModalProps> = ({ studentId, campusId, onClose }) => {
	const dispatch = useDispatch()
	const sessionToken: string | null = useSelector((state: RootState) => state.sessionToken.value)
	const basicInfo: any = useSelector((state: RootState) => state.basicInfo.value)
	const currentUser: any = useSelector((state: RootState) => state.userProfile.value)
	//Se puede editar sólo si el rol puede editar en Estudiantes; si no, se ve en modo lectura.
	const puedeEditar: boolean = !!getAccessPermissions(currentUser, "students").update

	const [grados, setGrados] = useState<any[] | null>(null)
	const [alumno, setAlumno] = useState<any>(null)
	const [fecha, setFecha] = useState<any>(null)

	const quejarse = (mensaje: string) => {
		dispatch(displayNotice({
			cornerClose: false, message: mensaje,
			heading: <h3 style={{ color: "var(--wa-rojo-error)", display: "inline-block" }}>Error</h3>
		}))
		onClose()
	}

	/*
		La ventana de Estudiantes es de otra librería (react-modal, z-index 100) y, abierta desde
		el ticket de Caja, quedaba DETRÁS de él. Mientras esta vive, sube por encima (ver app.css).
	*/
	useEffect(() => {
		document.body.classList.add("waPerfilEncima")
		return () => document.body.classList.remove("waPerfilEncima")
	}, [])

	useEffect(() => {
		const cargar = async () => {
			try {
				const [gradosRes, alumnoRes] = await Promise.all([
					CatalogService.getAcademicDegrees(sessionToken),
					StudentsService.searchStudents(campusId, {
						q: "", statusId: -1, tags: [], activeSchedule: 2, monthlyBalance: operatorList[0].code, productsBalance: operatorList[0].code,
						monthlyQuantity: 0, productsQuantity: 0, userId: studentId
					}, 1)
				])
				const record: any = alumnoRes.data?.data?.[0]

				if (record == null) {
					quejarse("El alumno no está en este plantel o no está disponible")
					return
				}
				const nacimiento: any = record.birthday == null ? undefined : record.birthday.replaceAll("-", "/")
				const ciudad: any = (record.state_id == null || record.city_id == null) ? null : { ...record.city, label: record.city?.name }
				const hashIds: any = new Hashids()
				const activas = record.enrollments.filter((e: any) => e.status_id == ENROLLMENT_STATUS.ACTIVE)
				const otras = record.enrollments.filter((e: any) => e.status_id != ENROLLMENT_STATUS.ACTIVE)

				setGrados(sinNinguno(gradosRes.data))
				setFecha(alumnoRes.data?.date)
				setAlumno({
					...record,
					birthday: nacimiento || "",
					birthdayValue: nacimiento == null ? "" : new Date(nacimiento + " 23:00:00"),
					city: ciudad,
					state_id: record.state_id == null ? "" : record.state_id,
					city_id: ciudad == null ? null : record.city_id,
					status_id: !record.bool_enabled ? 0 : record.student.status_id,
					cursos: cursosDeAlumno(record.enrollments, basicInfo?.enrollmentStatusRef),
					refIndex: "S_" + record.id,
					phone: record.phone || "",
					observations: record.student.observations || "",
					id_clase_365: record.id_clase_365 || "",
					zipcode: record.zipcode || "",
					correlativo_clase_365: record.student.correlativo_clase_365 || "",
					identity_code: record.student.identity_code || "",
					academic_degree: record.student.academic_degree,
					academic_degree_id: record.student.academic_degree?.id,
					specific_academic_degree: record.student.specific_academic_degree || "",
					last_school_name: record.student.last_school_name || "",
					current_occupation: record.student.current_occupation || "",
					password: "",
					contacts: ((record.student.contacts && JSON.parse(record.student.contacts)) || []),
					imagePreview: record.image,
					enrollments: conAgenda(activas, hashIds),
					nonActiveEnrollments: conAgenda(otras, hashIds),
					bool_read_only: puedeEditar ? 0 : 1
				})
			} catch (error) {
				console.log(error)
				quejarse("El perfil del alumno no pudo abrirse")
			}
		}
		cargar()
	}, [studentId, campusId])

	if (alumno == null || grados == null) {
		return <div className="loader" style={{ position: "fixed", top: "45%", left: "50%", zIndex: 3000 }} />
	}
	return (
		<StudentInfoModal
			selectedStudent={alumno}
			allowedUpdate={puedeEditar}
			academicDegrees={grados}
			closeFn={(actualizado?: any) => { if (actualizado == null) { onClose() } else { setAlumno(actualizado) } }}
			afterCloseFn={() => {}}
			date={fecha}
		/>
	)
}

export default PerfilAlumnoModal
